import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { GlobalSearch } from '@/components/shared/GlobalSearch';
import { LegalHoldBanner } from '@grc/shared-ui';
import { useLegalHoldStatus } from '@/api/hooks/useLegalHoldStatus';

export function AppShell() {
  const { data: holdStatus } = useLegalHoldStatus();

  // ⚠ RR-SCROLL-1: while the app shell is mounted, LOCK document scroll so `main` is the NECESSARY sole
  // scroller. The body/#root can then never scroll → the shell (the sidebar) can never move, no matter how
  // tall a page's content is. ⚠ SCOPED to AppShell (restored on unmount) so the portal/login/auditor routes
  // (min-h-screen, no inner scroller) KEEP their legitimate document scroll — the scope-check fix.
  useEffect(() => {
    const els = [document.documentElement, document.body];
    const prev = els.map((el) => ({ height: el.style.height, overflow: el.style.overflow }));
    for (const el of els) { el.style.height = '100%'; el.style.overflow = 'hidden'; }
    return () => {
      els.forEach((el, i) => { el.style.height = prev[i].height; el.style.overflow = prev[i].overflow; });
    };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-bg-base">
      {/* Sidebar */}
      <Sidebar />

      {/* Main content area — min-w-0 so wide content never widens the column; ⚠ RR-SCROLL-1:
          min-h-0 so this flex-col respects its 100vh bound (children scroll INSIDE, never grow the shell). */}
      <div className="flex min-w-0 min-h-0 flex-1 flex-col overflow-hidden">
        <Header />
        <LegalHoldBanner active={holdStatus?.active ?? false} />
        {/* ⚠ RR-SCROLL-1: min-h-0 + overflow-y-auto make `main` the sole, content-bounded scroller — the RA
            BI content scrolls INSIDE main (fully reachable, never clipped), and overscroll-contain stops any
            nested-list chain here so it can NEVER reach the (now-locked) document. */}
        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6">
          <Outlet />
        </main>
      </div>

      {/* Overlay (Dialog-portal — does not participate in the flex row) */}
      <GlobalSearch />
    </div>
  );
}
